/**
 * VenuePartnerDashboardPage.jsx — logged-in venue owner dashboard.
 *
 * Tabs: Overview · Menu editor (price / 86 / prep) · Staff PINs.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Utensils, Users, Calendar, ArrowRight, ChefHat, Salad, RefreshCw,
  Building2, MapPin, KeyRound, Trash2, Plus, DollarSign, Clock, Ban,
  CheckCircle2,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", {
    weekday: "short", day: "numeric", month: "short",
    hour: "numeric", minute: "2-digit", hour12: true }); }
  catch { return iso; }
};

export default function VenuePartnerDashboardPage() {
  const { vendorId } = useParams();
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState("");
  const [tab, setTab]     = useState("overview");
  const [busy, setBusy]   = useState(false);
  const [menu, setMenu]   = useState({ food: [], beverages: [] });
  const [policy, setPolicy] = useState({ format: "numeric", length: 4, session_hours: 8 });
  const [staff, setStaff]   = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", role: "server", pin: "" });
  const cfg = { headers: authHeader(), withCredentials: true };

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await axios.get(
        `${API}/hustlemeet-venue-partners/vendor/${vendorId}/dashboard`, cfg);
      setData(d); setErr("");
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const loadMenu = async () => {
    try {
      const { data: d } = await axios.get(
        `${API}/venue-terminal/${vendorId}/menu`, cfg);
      setMenu(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Menu load failed"); }
  };
  const loadPin = async () => {
    try {
      const { data: p } = await axios.get(`${API}/venue-pin/policy/${vendorId}`);
      setPolicy((s) => ({ ...s, ...p }));
    } catch (_) {}
    try {
      const { data: s } = await axios.get(`${API}/venue-pin/staff/${vendorId}`, cfg);
      setStaff(s.staff || []);
    } catch (_) {}
  };

  useEffect(() => { document.title = "Partner Dashboard · HustleMeetups"; load(); }, [vendorId]);
  useEffect(() => {
    if (tab === "menu")  loadMenu();
    if (tab === "staff") loadPin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const updateItem = async (item, patch) => {
    try {
      await axios.patch(
        `${API}/venue-terminal/${vendorId}/menu/${item.id}`, patch, cfg);
      toast.success(`${item.name} updated`);
      loadMenu();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const savePolicy = async (partial) => {
    try {
      const { data: p } = await axios.post(
        `${API}/venue-pin/policy/${vendorId}`, { ...policy, ...partial }, cfg);
      setPolicy((s) => ({ ...s, ...p }));
      toast.success("PIN policy updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Policy save failed"); }
  };
  const addStaff = async () => {
    try {
      await axios.post(
        `${API}/venue-pin/staff/${vendorId}`, newStaff, cfg);
      toast.success(`${newStaff.name} added`);
      setNewStaff({ name: "", role: "server", pin: "" });
      loadPin();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  const rmStaff = async (staffId) => {
    if (!window.confirm("Remove this staff PIN?")) return;
    try {
      await axios.delete(`${API}/venue-pin/staff/${vendorId}/${staffId}`, cfg);
      loadPin();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div data-testid="venue-partner-dashboard-page"
         className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100">
      <PlatformHeader brand={{ name: "HustleMeetups · Partner", to: "/meet" }}
                      showBookCta={false} showCart={false}/>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-6 flex flex-wrap items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Building2 size={20}/>
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">Partner venue dashboard</p>
            <h1 className="mt-1 text-2xl md:text-3xl font-black">
              {data?.partner?.venue_name || "Partner venue"}
            </h1>
            {data?.partner && (
              <p className="mt-1 text-xs text-stone-500 inline-flex items-center gap-1">
                <MapPin size={11}/> {data.partner.suburb} {data.partner.state} · Tier {data.partner.tier}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={load} variant="outline" disabled={busy}
                    data-testid="btn-refresh"
                    className="rounded-full font-black">
              <RefreshCw size={14} className="mr-1"/> Refresh
            </Button>
            <Button asChild data-testid="btn-open-terminal"
                    className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black">
              <Link to={`/venue-terminal/${vendorId}`}>
                Open Terminal <ArrowRight size={14} className="ml-1"/>
              </Link>
            </Button>
          </div>
        </header>

        {err && (
          <div data-testid="dashboard-err"
               className="rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-4 text-sm">
            {err}. You need to sign in with the vendor owner account.
          </div>
        )}

        <div data-testid="dashboard-tabs" className="flex flex-wrap gap-2 mb-4">
          {[
            ["overview", Building2, "Overview"],
            ["menu",     Utensils,  "Menu"],
            ["staff",    KeyRound,  "Staff PINs"],
          ].map(([k, Icon, label]) => (
            <button
              key={k}
              data-testid={`dash-tab-${k}`}
              onClick={() => setTab(k)}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black ${
                tab === k ? "bg-amber-500 text-slate-950"
                          : "bg-stone-100 dark:bg-stone-900 text-stone-500"}`}
            >
              <Icon size={12}/> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && data && (
          <>
            <section data-testid="dashboard-totals"
                     className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                [Building2, "Circles hosted",     data.totals.circle_count],
                [Calendar,  "Events next 30 days",data.totals.event_count],
                [Users,     "Covers next 30 days",data.totals.cover_total],
                [ChefHat,   "Approved tier",      (data.partner?.tier || "").replace("_", " ")],
              ].map(([Icon, label, value], i) => (
                <div key={i}
                     className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4">
                  <Icon size={18} className="text-amber-500"/>
                  <p className="mt-2 text-[10px] uppercase tracking-widest font-black text-stone-500">{label}</p>
                  <p className="mt-1 text-2xl font-black">{value ?? "—"}</p>
                </div>
              ))}
            </section>

            <section data-testid="dashboard-events"
                     className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={16} className="text-amber-500"/>
                <h2 className="text-sm font-black uppercase tracking-widest text-stone-500">
                  Next 30 days — bookings, covers, dietary
                </h2>
              </div>
              {data.next_30_days.length === 0 ? (
                <p className="text-sm text-stone-500">Nothing on the books yet — Circles will book you as soon as the next planning window opens.</p>
              ) : (
                <ul className="divide-y divide-stone-200 dark:divide-stone-800">
                  {data.next_30_days.map((ev) => {
                    const diet = ev.dietary || {};
                    const dietTags = Object.entries(diet).filter(([, n]) => n).map(([k, n]) => `${k}:${n}`);
                    return (
                      <li key={ev.id}
                          data-testid={`dashboard-event-${ev.id}`}
                          className="py-3 flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[220px]">
                          <p className="text-sm font-black">{ev.title || "Circle dinner"}</p>
                          <p className="text-[11px] text-stone-500 inline-flex items-center gap-1">
                            <Calendar size={11}/> {fmtDT(ev.starts_at)}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1 text-[11px] font-black text-emerald-600">
                          <Users size={12}/> {ev.attendees}/{ev.capacity} covers
                        </span>
                        {dietTags.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-black text-amber-600">
                            <Salad size={12}/> {dietTags.join(" · ")}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section data-testid="dashboard-circles"
                     className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-amber-500"/>
                <h2 className="text-sm font-black uppercase tracking-widest text-stone-500">
                  Circles hosted at your venue
                </h2>
              </div>
              {data.circles.length === 0 ? (
                <p className="text-sm text-stone-500">No Circles yet.</p>
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.circles.map((c) => (
                    <li key={c.id}
                        data-testid={`dashboard-circle-${c.id}`}
                        className="rounded-lg border border-stone-200 dark:border-stone-800 p-3">
                      <p className="text-sm font-black">{c.name}</p>
                      <p className="text-[11px] text-stone-500 line-clamp-2">{c.tagline}</p>
                      <p className="mt-2 text-[10px] uppercase tracking-widest text-emerald-600 font-black">
                        {c.cadence} · {c.default_party_size} seats
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}

        {tab === "menu" && (
          <section data-testid="dashboard-menu-editor"
                   className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Utensils size={16} className="text-amber-500"/>
              <h2 className="text-sm font-black uppercase tracking-widest text-stone-500">
                Menu editor — price, 86-out, prep time
              </h2>
            </div>
            {[...(menu.food || []), ...(menu.beverages || [])].length === 0 ? (
              <p className="text-sm text-stone-500">No menu items yet.</p>
            ) : (
              <div className="space-y-2">
                {[...(menu.food || []), ...(menu.beverages || [])].map((it) => (
                  <MenuRow key={it.id} item={it} onUpdate={updateItem}/>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "staff" && (
          <>
            <section data-testid="dashboard-pin-policy"
                     className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound size={16} className="text-amber-500"/>
                <h2 className="text-sm font-black uppercase tracking-widest text-stone-500">PIN policy</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                  Character set
                  <select data-testid="pin-format-select"
                          value={policy.format}
                          onChange={(e) => savePolicy({ format: e.target.value })}
                          className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent">
                    <option value="numeric">Numeric only</option>
                    <option value="alpha">Alpha only</option>
                    <option value="alphanumeric">Alphanumeric</option>
                  </select>
                </label>
                <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                  Length
                  <select data-testid="pin-length-select"
                          value={policy.length}
                          onChange={(e) => savePolicy({ length: parseInt(e.target.value, 10) })}
                          className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent">
                    <option value={4}>4 characters</option>
                    <option value={6}>6 characters</option>
                  </select>
                </label>
                <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                  Session length (hours)
                  <input type="number" min={1} max={24}
                         data-testid="pin-session-hours"
                         value={policy.session_hours || 8}
                         onChange={(e) => setPolicy((p) => ({ ...p, session_hours: parseInt(e.target.value, 10) }))}
                         onBlur={() => savePolicy({ session_hours: policy.session_hours })}
                         className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
                </label>
              </div>
              <p className="mt-3 text-[11px] text-stone-500">
                Every completed transaction re-locks the kiosk so the next staff member enters their PIN.
              </p>
            </section>

            <section data-testid="dashboard-staff-list"
                     className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-amber-500"/>
                <h2 className="text-sm font-black uppercase tracking-widest text-stone-500">
                  Staff PINs ({staff.length})
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                {staff.map((s) => (
                  <div key={s.id}
                       data-testid={`staff-row-${s.id}`}
                       className="rounded-lg border border-stone-200 dark:border-stone-800 p-3 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black truncate">{s.name}</p>
                      <p className="text-[10px] uppercase tracking-widest text-stone-500">{s.role}</p>
                    </div>
                    <button data-testid={`btn-remove-staff-${s.id}`}
                            onClick={() => rmStaff(s.id)}
                            className="text-red-500 hover:opacity-70">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-dashed border-stone-300 dark:border-stone-700 p-3 bg-stone-50/50 dark:bg-stone-900/50">
                <p className="text-xs font-black uppercase tracking-widest text-stone-500 mb-2">Add staff PIN</p>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <input data-testid="new-staff-name" placeholder="Name"
                         value={newStaff.name}
                         onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                         className="rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
                  <select data-testid="new-staff-role"
                          value={newStaff.role}
                          onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                          className="rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent">
                    <option value="server">Server</option>
                    <option value="manager">Manager</option>
                    <option value="chef">Chef</option>
                    <option value="kiosk">Kitchen kiosk</option>
                  </select>
                  <input data-testid="new-staff-pin"
                         placeholder={`${policy.length}-char PIN`}
                         value={newStaff.pin}
                         onChange={(e) => setNewStaff({ ...newStaff, pin: e.target.value })}
                         className="rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
                  <Button data-testid="btn-add-staff"
                          onClick={addStaff}
                          disabled={!newStaff.name || !newStaff.pin}
                          className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black">
                    <Plus size={14} className="mr-1"/> Add
                  </Button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
      <SuiteFooter />
    </div>
  );
}

function MenuRow({ item, onUpdate }) {
  const [price, setPrice] = useState(item.price || 0);
  const [prep, setPrep]   = useState(item.prep_time_minutes || 10);
  const eighty = !!item.eighty_six;
  return (
    <div data-testid={`menu-row-${item.id}`}
         className={`grid grid-cols-1 sm:grid-cols-12 items-center gap-2 rounded-lg border p-3 ${
           eighty ? "border-red-300 bg-red-50/50 dark:bg-red-950/20" : "border-stone-200 dark:border-stone-800"}`}>
      <div className="sm:col-span-4">
        <p className="text-sm font-black">{item.name}</p>
        <p className="text-[10px] uppercase tracking-widest text-stone-500">{item.category}</p>
      </div>
      <label className="sm:col-span-3 text-[10px] uppercase tracking-widest text-stone-500 font-black flex items-center gap-2">
        <DollarSign size={11}/>
        <input data-testid={`price-${item.id}`}
               type="number" step="0.5"
               value={price}
               onChange={(e) => setPrice(parseFloat(e.target.value))}
               onBlur={() => price !== item.price && onUpdate(item, { price })}
               className="rounded border border-stone-200 dark:border-stone-700 px-2 py-1 text-sm bg-transparent w-20"/>
      </label>
      <label className="sm:col-span-3 text-[10px] uppercase tracking-widest text-stone-500 font-black flex items-center gap-2">
        <Clock size={11}/>
        <input data-testid={`prep-${item.id}`}
               type="number" min={1} max={90}
               value={prep}
               onChange={(e) => setPrep(parseInt(e.target.value, 10))}
               onBlur={() => prep !== item.prep_time_minutes && onUpdate(item, { prep_time_minutes: prep })}
               className="rounded border border-stone-200 dark:border-stone-700 px-2 py-1 text-sm bg-transparent w-16"/>
        min
      </label>
      <div className="sm:col-span-2">
        <button data-testid={`btn-86-${item.id}`}
                onClick={() => onUpdate(item, { eighty_six: !eighty })}
                className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-black inline-flex items-center gap-1 ${
                  eighty ? "bg-red-500 text-white"
                         : "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25"}`}>
          {eighty ? <><Ban size={11}/> 86'd</> : <><CheckCircle2 size={11}/> Live</>}
        </button>
      </div>
    </div>
  );
}
